/**
 * One graded slot in the case panel's list, and the hint that explains its
 * colour.
 *
 * Split out of SlotList.tsx, which went over the repo's 500-line cap once the
 * final-round holds work and the ask-selection fixes landed in the same file.
 * Nothing changed in the move.
 */
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn-auto/design-system/atoms/select';
import type { GradedSlot, PanelConfig } from '../../types/taActionBoard';
import {
  COLORS,
  firstNameFromEmail,
  fmtSlotRange,
  fmtTimeOnly,
  fullNameFromEmail,
  seatedPanelFor,
} from './format';

/** One slot row: dot + range + what it costs, checkbox on the right. */
export function SlotRow({
  slot,
  panel,
  checked,
  blocked,
  chosenStart,
  onChooseStart,
  onToggle,
}: {
  slot: GradedSlot;
  panel: PanelConfig | null;
  checked: boolean;
  /**
   * Already held. Shown as such, but still TICKABLE: the ticks also drive
   * "Ask panel" and the reply proposal, and locking the row made a held slot
   * un-askable — exactly the slot a recruiter most wants the panel's OK on.
   * Re-holding it is what stays impossible, and the hold button enforces that.
   */
  blocked: boolean;
  /** Which start is selected inside a grouped free window */
  chosenStart: string;
  onChooseStart: (start: string) => void;
  onToggle: () => void;
}) {
  const options = slot.startOptions ?? [];
  const isWindow = options.length > 1;
  // A window shows the whole free stretch; a single slot shows the interview itself.
  const range = isWindow && slot.windowEnd
    ? fmtSlotRange({ ...slot, end: slot.windowEnd })
    : fmtSlotRange(slot);
  // Nothing locks the checkbox any more — see the `blocked` note above.
  return (
    <li className="flex items-center gap-2 rounded-md bg-white px-2.5 py-1.5 text-xs text-gray-800 ring-1 ring-gray-100">
      {/* aria-hidden: the SlotHint beside it states the grade in words ("all free",
          "… would need to move"), so the dot is a supplemental colour cue only. */}
      <span
        aria-hidden
        className="inline-block h-2 w-2 shrink-0 rounded-full"
        style={{
          backgroundColor:
            slot.grade === 'free'
              ? COLORS.statusGreen
              : slot.grade === 'needs-move'
                ? COLORS.statusOrange
                : COLORS.statusBlue,
        }}
      />
      <span className="font-semibold">{range}</span>
      {isWindow && (
        <Select value={chosenStart} onValueChange={onChooseStart}>
          {/* The DS Select carries its own type scale, so `text-xs` on the
              trigger alone left a 16px value towering over an 12px row. The
              child selector pins the rendered value too. */}
          <SelectTrigger
            aria-label="Start time within this free window"
            className="h-6 w-auto gap-1 px-2 py-0 text-xs [&>span]:text-xs [&_svg]:h-3 [&_svg]:w-3"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o} value={o} className="text-xs">
                start {fmtTimeOnly(o)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <PanelChips slot={slot} panel={panel} />
      <SlotHint slot={slot} panel={panel} />
      <span className="ml-auto flex shrink-0 items-center gap-2">
        {blocked && (
          <span className="text-gray-400" title="Already on hold — release it under “Holds” above">
            on hold
          </span>
        )}
        {/* A held slot keeps its checkbox: the ticks drive "Ask panel" and the
            reply proposal as well as the hold button, and only the hold button
            refuses an already-held slot. */}
        <Checkbox
          checked={checked}
          onCheckedChange={onToggle}
          aria-label={`Select ${range}`}
        />
      </span>
    </li>
  );
}

/**
 * Who would run the interview at THIS slot.
 *
 * On every row, including the free ones: the constellation is the thing a
 * recruiter is choosing between when they compare date options, and before this
 * it was only inferable — from the panel line at the top of the section, minus
 * whatever a truncated hint managed to say about a stand-in.
 *
 * `shrink-0` on purpose: the hint beside it truncates and carries a `title`,
 * whereas a half-shown panel would be actively misleading about who is in the
 * room.
 */
function PanelChips({ slot, panel }: { slot: GradedSlot; panel: PanelConfig | null }) {
  const seats = seatedPanelFor(slot, panel?.interviewers);
  if (seats.length === 0) return null;
  return (
    <span className="flex shrink-0 items-center gap-1">
      {seats.map((seat) => (
        <span
          key={seat.email}
          title={
            seat.steppedInFor
              ? `${fullNameFromEmail(seat.email)} steps in for ${fullNameFromEmail(seat.steppedInFor)}`
              : fullNameFromEmail(seat.email)
          }
          className={
            seat.steppedInFor
              ? 'rounded-full bg-blue-50 px-1.5 py-0.5 text-[11px] font-medium text-blue-700 ring-1 ring-blue-100'
              : 'rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600'
          }
        >
          {firstNameFromEmail(seat.email)}
          {seat.steppedInFor ? ' ⇄' : ''}
        </span>
      ))}
    </span>
  );
}

function SlotHint({ slot, panel }: { slot: GradedSlot; panel: PanelConfig | null }) {
  if (slot.grade === 'free') return <span className="truncate text-gray-400">all free</span>;
  if (slot.grade === 'needs-move') {
    const fallbackNote =
      slot.fallbackOption && panel?.fallback
        ? ` · or ${firstNameFromEmail(slot.fallbackOption.in)} steps in for ${firstNameFromEmail(slot.fallbackOption.out)}`
        : '';
    /**
     * EVERY meeting that would have to move is named. It used to print the
     * first plus "(+2 more)", which is the one thing a recruiter cannot act on:
     * whether a yellow slot is worth proposing depends entirely on WHICH
     * meetings move and whose they are (Theresa, Sep 2026). `title` (not
     * `truncate`) keeps the row one line while the full list stays readable on
     * hover.
     */
    const moves = slot.conflicts.map(
      (c) => `${firstNameFromEmail(c.email)}: “${c.title}” would need to move`
    );
    return (
      <span className="truncate text-gray-500" title={[...moves, fallbackNote].filter(Boolean).join(' · ')}>
        {moves.join(' · ')}
        {fallbackNote}
      </span>
    );
  }
  const swap = slot.replaced || slot.fallbackOption;
  return (
    <span className="truncate text-gray-500">
      {swap && (
        <>
          {fullNameFromEmail(swap.in)} steps in for {firstNameFromEmail(swap.out)}
        </>
      )}
    </span>
  );
}
